// src/modules/app-views/fiscal-config/pages/Document/tabs/CertificateTab/constants.js

// =============================================================================
// VALIDATION CONSTANTS - Allowed CST/CSOSN for company defaults
// =============================================================================
// Company defaults CANNOT include ST-specific codes because ST depends on
// the product characteristics, not company-wide settings.
// Using ST codes as company default would be a fiscal/legal risk.

// Allowed ICMS CST for company defaults (Regime Normal - CRT 3)
// EXCLUDED: 10 (ST), 30 (Isenta com ST), 60 (ICMS cobrado por ST), 70 (Redução BC com ST)
export const ALLOWED_DEFAULT_ICMS_CST = ['00', '20', '40', '41', '50', '51', '90'];

// Allowed ICMS CSOSN for company defaults (Simples Nacional - CRT 1/2)
// EXCLUDED: 201, 202, 203 (com ST), 500 (ICMS cobrado por ST)
export const ALLOWED_DEFAULT_ICMS_CSOSN = ['101', '102', '103', '300', '400', '900'];

// =============================================================================
// NOTE: ICMS_ORIGIN_OPTIONS removed from company level
// Origin (0-8) refers to the PRODUCT origin (national/imported), not company.
// This field should only exist in Product and Invoice Item levels.
// =============================================================================

// =============================================================================
// NOTE: CFOP_OPTIONS removed from company level
// CFOP depends on the OPERATION TYPE (sale, return, transfer, bonus, etc.)
// Having a "default CFOP" at company level is a fiscal/legal risk because
// it could apply wrong CFOP to different operations.
// CFOP should be defined at Product level (as suggestion) and Invoice Item level (final).
// =============================================================================

// IPI CST options
export const IPI_CST_OPTIONS = [
  { value: '50', label: '50 - Saída tributada' },
  { value: '51', label: '51 - Saída tributada com alíquota zero' },
  { value: '52', label: '52 - Saída isenta' },
  { value: '53', label: '53 - Saída não tributada' },
  { value: '54', label: '54 - Saída imune' },
  { value: '55', label: '55 - Saída com suspensão' },
  { value: '99', label: '99 - Outras saídas' },
];

export const TAX_REGIME_OPTIONS = [
  { value: 'simples_nacional', label: 'Simples Nacional (CRT 1)' },
  { value: 'simples_nacional_excesso', label: 'Simples Nacional - Excesso (CRT 2)' },
  { value: 'regime_normal', label: 'Regime Normal (CRT 3)' },
];

export const ENVIRONMENT_OPTIONS = [
  { value: 'homologation', label: 'Homologação (Testes)' },
  { value: 'production', label: 'Produção' },
];

// Fiscal Providers (Motores Fiscais)
// NFe / NFCe — amaru (FocusNFe) is the primary provider; pachamama (NuvemFiscal) available
export const FISCAL_PROVIDER_OPTIONS = [
  { value: 'amaru', label: 'amaru' },
  { value: 'pachamama', label: 'pachamama' },
];

// NFSe — amaru does not support NFSe yet; pachamama (NuvemFiscal) only
export const NFSE_PROVIDER_OPTIONS = [{ value: 'pachamama', label: 'pachamama' }];

// Thermal Printers for NFC-e - Full configuration (for printing logic)
export const THERMAL_PRINTER_CONFIG = [
  {
    value: 'epson_tm_t20x',
    label: 'Epson TM-T20X',
    vendorId: 0x04b8,
    productId: 0x0202,
    alternativeProductIds: [0x0202, 0x0e15, 0x0e28, 0x0e03, 0x0e04],
    baudRate: 9600,
  },
  {
    value: 'epson_tm_t20',
    label: 'Epson TM-T20',
    vendorId: 0x04b8,
    productId: 0x0e03,
    alternativeProductIds: [0x0e03],
    baudRate: 9600,
  },
  {
    value: 'epson_tm_t20ii',
    label: 'Epson TM-T20II',
    vendorId: 0x04b8,
    productId: 0x0e15,
    alternativeProductIds: [0x0e15],
    baudRate: 9600,
  },
  {
    value: 'epson_tm_t88v',
    label: 'Epson TM-T88V',
    vendorId: 0x04b8,
    productId: 0x0202,
    alternativeProductIds: [0x0202],
    baudRate: 9600,
  },
  {
    value: 'bematech_mp4200th',
    label: 'Bematech MP-4200 TH',
    vendorId: 0x0b1b,
    productId: 0x0003,
    alternativeProductIds: [0x0003, 0x0001, 0x0002],
    baudRate: 115200,
  },
  {
    value: 'elgin_i9',
    label: 'Elgin i9',
    vendorId: 0x0dd4,
    productId: 0x0200,
    alternativeProductIds: [0x0200],
    baudRate: 115200,
  },
];

// Thermal Printers for NFC-e - Select options (for UI dropdowns)
export const THERMAL_PRINTER_OPTIONS = THERMAL_PRINTER_CONFIG.map(({ value, label }) => ({
  value,
  label,
}));

// ICMS CST for Regime Normal (CRT 3)
export const ICMS_CST_OPTIONS = [
  { value: '00', label: '00 - Tributada integralmente' },
  { value: '10', label: '10 - Tributada com ST' },
  { value: '20', label: '20 - Com redução de BC' },
  { value: '30', label: '30 - Isenta/não tributada com ST' },
  { value: '40', label: '40 - Isenta' },
  { value: '41', label: '41 - Não tributada' },
  { value: '50', label: '50 - Com suspensão' },
  { value: '51', label: '51 - Com diferimento' },
  { value: '60', label: '60 - ICMS cobrado por ST' },
  { value: '70', label: '70 - Redução BC com ST' },
  { value: '90', label: '90 - Outras' },
];

// ICMS CSOSN for Simples Nacional (CRT 1/2)
export const ICMS_CSOSN_OPTIONS = [
  { value: '101', label: '101 - Tributada com crédito' },
  { value: '102', label: '102 - Tributada sem crédito' },
  { value: '103', label: '103 - Isenção para faixa receita' },
  { value: '201', label: '201 - Com crédito e ST' },
  { value: '202', label: '202 - Sem crédito e ST' },
  { value: '203', label: '203 - Isenção com ST' },
  { value: '300', label: '300 - Imune' },
  { value: '400', label: '400 - Não tributada' },
  { value: '500', label: '500 - ICMS cobrado por ST' },
  { value: '900', label: '900 - Outros' },
];

// PIS CST
export const PIS_CST_OPTIONS = [
  { value: '01', label: '01 - Alíquota básica' },
  { value: '02', label: '02 - Alíquota diferenciada' },
  { value: '03', label: '03 - Alíquota por unidade' },
  { value: '04', label: '04 - Monofásica - Alíquota zero' },
  { value: '05', label: '05 - Substituição tributária' },
  { value: '06', label: '06 - Alíquota zero' },
  { value: '07', label: '07 - Isenta' },
  { value: '08', label: '08 - Sem incidência' },
  { value: '09', label: '09 - Com suspensão' },
  { value: '49', label: '49 - Outras saídas' },
  { value: '99', label: '99 - Outras operações' },
];

// COFINS CST (same structure as PIS)
export const COFINS_CST_OPTIONS = [
  { value: '01', label: '01 - Alíquota básica' },
  { value: '02', label: '02 - Alíquota diferenciada' },
  { value: '03', label: '03 - Alíquota por unidade' },
  { value: '04', label: '04 - Monofásica - Alíquota zero' },
  { value: '05', label: '05 - Substituição tributária' },
  { value: '06', label: '06 - Alíquota zero' },
  { value: '07', label: '07 - Isenta' },
  { value: '08', label: '08 - Sem incidência' },
  { value: '09', label: '09 - Com suspensão' },
  { value: '49', label: '49 - Outras saídas' },
  { value: '99', label: '99 - Outras operações' },
];

// Calculate gradient color based on progress (green -> orange -> red)
export function getProgressColor(percent) {
  if (percent <= 50) {
    // Green to Orange (0-50%)
    const ratio = percent / 50;
    const r = Math.round(82 + (250 - 82) * ratio);
    const g = Math.round(196 + (173 - 196) * ratio);
    const b = Math.round(26 + (20 - 26) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Orange to Red (50-100%)
    const ratio = (percent - 50) / 50;
    const r = Math.round(250 + (245 - 250) * ratio);
    const g = Math.round(173 + (34 - 173) * ratio);
    const b = Math.round(20 + (45 - 20) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  }
}
